import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../services/api.service';
import { Field, FieldText } from '../../models/field';
import { AppSettings } from '../../models/app-settings';

interface FieldTextImage {
  id: string;
  name: string;
  imageDataUrl: string;
}

interface FieldTextLink {
  id: string;
  label: string;
  url: string;
}

@Component({
  selector: 'app-configuration',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './configuration.component.html',
  styleUrls: ['./configuration.component.scss']
})
export class ConfigurationComponent implements OnInit {
  fields: Field[] = [];
  selectedFieldId: string | null = null;
  selectedFieldTexts: FieldText[] = [];
  isLoading = false;
  fieldTextsLoading = false;
  errorMessage = '';
  confirmDeleteId: string | null = null;
  confirmDeleteTextId: string | null = null;
  textImageUploadError = '';
  textImageDropActive = false;
  textFormImages: FieldTextImage[] = [];
  textFormLinks: FieldTextLink[] = [];
  newTextLinkLabel = '';
  newTextLinkUrl = '';
  detailText: FieldText | null = null;
  activeDetailTabId = '';
  appSettings: AppSettings = { aiJsonInputsEnabled: false };
  appSettingsLoading = false;
  appSettingsSaving = false;

  editMode: 'create' | 'update' = 'create';
  formModel: Field = {
    feldId: '',
    feldBezeichnung: ''
  };
  textEditMode: 'create' | 'update' = 'create';
  textFormModel: FieldText = {
    feldTextId: '',
    bezeichnung: '',
    beschreibung: '',
    bilderJson: '',
    anzeigen: true,
    crvGrund: undefined,
    marge: undefined,
    kostenProEinheit: undefined
  };

  constructor(private api: ApiService) {}

  ngOnInit(): void {
    void this.loadFields();
    void this.loadAppSettings();
  }

  async loadAppSettings(): Promise<void> {
    this.appSettingsLoading = true;
    try {
      this.appSettings = await firstValueFrom(this.api.getAppSettings());
    } catch {
      this.appSettings = { aiJsonInputsEnabled: false };
      this.errorMessage = 'Administratoreinstellungen konnten nicht geladen werden.';
    } finally {
      this.appSettingsLoading = false;
    }
  }

  async saveAppSettings(): Promise<void> {
    this.appSettingsSaving = true;
    try {
      this.appSettings = await firstValueFrom(this.api.updateAppSettings(this.appSettings));
    } catch {
      this.errorMessage = 'Administratoreinstellungen konnten nicht gespeichert werden.';
    } finally {
      this.appSettingsSaving = false;
    }
  }

  async loadFields(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';
    try {
      this.fields = await firstValueFrom(this.api.getFields());
      if (!this.fields.some((field) => field.feldId === this.selectedFieldId)) {
        this.selectedFieldId = null;
        this.selectedFieldTexts = [];
      }
    } catch {
      this.errorMessage = 'Felder konnten nicht geladen werden.';
      this.fields = [];
      this.selectedFieldId = null;
      this.selectedFieldTexts = [];
    } finally {
      this.isLoading = false;
    }
  }

  isSelected(field: Field): boolean {
    return this.selectedFieldId === field.feldId;
  }

  async selectField(field: Field): Promise<void> {
    this.selectedFieldId = field.feldId;
    this.confirmDeleteId = null;
    this.fieldTextsLoading = true;
    this.errorMessage = '';
    try {
      this.selectedFieldTexts = await firstValueFrom(this.api.getFieldTexts(field.feldId));
    } catch {
      this.selectedFieldTexts = [];
      this.errorMessage = 'Feldtexte konnten nicht geladen werden.';
    } finally {
      this.fieldTextsLoading = false;
    }

    this.startTextCreate();
  }

  startCreate(): void {
    this.editMode = 'create';
    this.formModel = {
      feldId: '',
      feldBezeichnung: ''
    };
    this.confirmDeleteId = null;
  }

  startTextCreate(): void {
    this.textEditMode = 'create';
    this.textFormModel = {
      feldTextId: '',
      bezeichnung: '',
      beschreibung: '',
      bilderJson: '',
      anzeigen: true,
      crvGrund: undefined,
      marge: undefined,
      kostenProEinheit: undefined
    };
    this.textFormImages = [];
    this.textFormLinks = [];
    this.newTextLinkLabel = '';
    this.newTextLinkUrl = '';
    this.textImageUploadError = '';
    this.textImageDropActive = false;
    this.confirmDeleteTextId = null;
  }

  startEdit(field: Field): void {
    this.editMode = 'update';
    this.formModel = {
      feldId: field.feldId,
      feldBezeichnung: field.feldBezeichnung
    };
    this.confirmDeleteId = null;
  }

  startTextEdit(fieldText: FieldText): void {
    this.textEditMode = 'update';
    this.textFormModel = {
      feldTextId: fieldText.feldTextId,
      bezeichnung: fieldText.bezeichnung,
      beschreibung: fieldText.beschreibung || '',
      bilderJson: fieldText.bilderJson || '',
      anzeigen: fieldText.anzeigen !== false,
      crvGrund: fieldText.crvGrund,
      marge: fieldText.marge,
      kostenProEinheit: fieldText.kostenProEinheit
    };
    this.textFormImages = this.parseFieldTextImages(fieldText);
    this.textFormLinks = this.parseFieldTextLinks(fieldText);
    this.newTextLinkLabel = '';
    this.newTextLinkUrl = '';
    this.textImageUploadError = '';
    this.textImageDropActive = false;
    this.confirmDeleteTextId = null;
  }

  canSave(): boolean {
    return !!this.formModel.feldId.trim() && !!this.formModel.feldBezeichnung.trim();
  }

  canSaveText(): boolean {
    return !!this.selectedFieldId && !!this.textFormModel.feldTextId.trim() && !!this.textFormModel.bezeichnung.trim();
  }

  async saveField(): Promise<void> {
    if (!this.canSave()) {
      return;
    }

    const payload: Field = {
      feldId: this.formModel.feldId.trim(),
      feldBezeichnung: this.formModel.feldBezeichnung.trim()
    };

    this.errorMessage = '';
    try {
      if (this.editMode === 'create') {
        await firstValueFrom(this.api.createField(payload));
      } else {
        await firstValueFrom(this.api.updateField(payload.feldId, payload));
      }

      await this.loadFields();
      this.editMode = 'create';
      this.formModel = { feldId: '', feldBezeichnung: '' };
      if (payload.feldId) {
        const selected = this.fields.find((field) => field.feldId === payload.feldId);
        if (selected) {
          await this.selectField(selected);
        }
      }
    } catch {
      this.errorMessage = this.editMode === 'create'
        ? 'Feld konnte nicht angelegt werden.'
        : 'Feld konnte nicht aktualisiert werden.';
    }
  }

  requestDelete(field: Field): void {
    this.confirmDeleteId = field.feldId;
  }

  cancelDelete(): void {
    this.confirmDeleteId = null;
  }

  async deleteField(field: Field): Promise<void> {
    this.errorMessage = '';
    try {
      await firstValueFrom(this.api.deleteField(field.feldId));
      this.confirmDeleteId = null;
      await this.loadFields();
    } catch {
      this.errorMessage = 'Feld konnte nicht gelöscht werden.';
    }
  }

  requestDeleteText(fieldText: FieldText): void {
    this.confirmDeleteTextId = fieldText.feldTextId;
  }

  cancelDeleteText(): void {
    this.confirmDeleteTextId = null;
  }

  async saveFieldText(): Promise<void> {
    if (!this.canSaveText() || !this.selectedFieldId) {
      return;
    }

    const payload: FieldText = {
      feldTextId: this.textFormModel.feldTextId.trim(),
      bezeichnung: this.textFormModel.bezeichnung.trim(),
      beschreibung: this.textFormModel.beschreibung?.trim() || '',
      bilderJson: this.createFieldTextMediaJson(),
      anzeigen: this.textFormModel.anzeigen !== false,
      crvGrund: this.toOptionalNumber(this.textFormModel.crvGrund),
      marge: this.toOptionalNumber(this.textFormModel.marge),
      kostenProEinheit: this.toOptionalNumber(this.textFormModel.kostenProEinheit)
    };

    this.errorMessage = '';
    try {
      if (this.textEditMode === 'create') {
        await firstValueFrom(this.api.createFieldText(this.selectedFieldId, payload));
      } else {
        await firstValueFrom(this.api.updateFieldText(this.selectedFieldId, payload.feldTextId, payload));
      }

      const selectedField = this.fields.find((field) => field.feldId === this.selectedFieldId);
      if (selectedField) {
        await this.selectField(selectedField);
      }
      this.startTextCreate();
    } catch {
      this.errorMessage = this.textEditMode === 'create'
        ? 'Feldtext konnte nicht angelegt werden.'
        : 'Feldtext konnte nicht aktualisiert werden.';
    }
  }

  async deleteFieldText(fieldText: FieldText): Promise<void> {
    if (!this.selectedFieldId) {
      return;
    }

    this.errorMessage = '';
    try {
      await firstValueFrom(this.api.deleteFieldText(this.selectedFieldId, fieldText.feldTextId));
      this.confirmDeleteTextId = null;
      const selectedField = this.fields.find((field) => field.feldId === this.selectedFieldId);
      if (selectedField) {
        await this.selectField(selectedField);
      }
      this.startTextCreate();
    } catch {
      this.errorMessage = 'Feldtext konnte nicht gelöscht werden.';
    }
  }

  private toOptionalNumber(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : undefined;
  }

  openTextDetails(fieldText: FieldText): void {
    this.detailText = fieldText;
    this.activeDetailTabId = this.detailTabs(fieldText)[0]?.id || '';
  }

  closeTextDetails(): void {
    this.detailText = null;
  }

  detailImages(): FieldTextImage[] {
    return this.detailText ? this.parseFieldTextImages(this.detailText) : [];
  }

  detailLinks(): FieldTextLink[] {
    return this.detailText ? this.parseFieldTextLinks(this.detailText) : [];
  }

  detailTabs(fieldText = this.detailText): Array<{ id: string; label: string; type: 'image' | 'links'; image?: FieldTextImage }> {
    if (!fieldText) return [];
    const imageTabs = this.parseFieldTextImages(fieldText).map((image, index) => ({
      id: image.id,
      label: image.name || `Bild ${index + 1}`,
      type: 'image' as const,
      image
    }));
    const links = this.parseFieldTextLinks(fieldText);
    return links.length
      ? [...imageTabs, { id: 'links', label: `Links (${links.length})`, type: 'links' as const }]
      : imageTabs;
  }

  activeDetailTab(): { id: string; label: string; type: 'image' | 'links'; image?: FieldTextImage } | null {
    const tabs = this.detailTabs();
    return tabs.find((tab) => tab.id === this.activeDetailTabId) ?? tabs[0] ?? null;
  }

  setActiveDetailTab(id: string): void {
    this.activeDetailTabId = id;
  }

  hasTextDetails(fieldText: FieldText): boolean {
    return !!fieldText.beschreibung?.trim()
      || this.parseFieldTextImages(fieldText).length > 0
      || this.parseFieldTextLinks(fieldText).length > 0;
  }

  textImageCount(fieldText: FieldText): number {
    return this.parseFieldTextImages(fieldText).length;
  }

  textLinkCount(fieldText: FieldText): number {
    return this.parseFieldTextLinks(fieldText).length;
  }

  onTextImageDragOver(event: DragEvent): void {
    event.preventDefault();
    this.textImageDropActive = true;
  }

  onTextImageDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.textImageDropActive = false;
  }

  handleTextImageDrop(event: DragEvent): void {
    event.preventDefault();
    this.textImageDropActive = false;
    this.processTextImageFiles(event.dataTransfer?.files);
  }

  handleTextImageFileSelection(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.processTextImageFiles(input.files);
    input.value = '';
  }

  handleTextImagePaste(event: ClipboardEvent): void {
    const files = Array.from(event.clipboardData?.items ?? [])
      .filter((item) => item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter((file): file is File => !!file);

    if (files.length) {
      event.preventDefault();
      this.processTextImageFiles(files);
    }
  }

  removeTextImage(id: string): void {
    this.textFormImages = this.textFormImages.filter((image) => image.id !== id);
  }

  addTextLink(): void {
    const url = this.newTextLinkUrl.trim();
    if (!url) return;

    this.textFormLinks = [
      ...this.textFormLinks,
      {
        id: this.createClientId(),
        label: this.newTextLinkLabel.trim() || url,
        url
      }
    ];
    this.newTextLinkLabel = '';
    this.newTextLinkUrl = '';
  }

  removeTextLink(id: string): void {
    this.textFormLinks = this.textFormLinks.filter((link) => link.id !== id);
  }

  async removeDetailImage(imageId: string): Promise<void> {
    if (!this.selectedFieldId || !this.detailText) return;

    const images = this.parseFieldTextImages(this.detailText).filter((image) => image.id !== imageId);
    const links = this.parseFieldTextLinks(this.detailText);
    const payload: FieldText = {
      ...this.detailText,
      bilderJson: this.createFieldTextMediaJson(images, links)
    };

    this.errorMessage = '';
    try {
      const saved = await firstValueFrom(this.api.updateFieldText(this.selectedFieldId, payload.feldTextId, payload));
      this.selectedFieldTexts = this.selectedFieldTexts.map((text) => text.feldTextId === saved.feldTextId ? saved : text);
      this.detailText = saved;
      this.textFormImages = this.textEditMode === 'update' && this.textFormModel.feldTextId === saved.feldTextId
        ? this.parseFieldTextImages(saved)
        : this.textFormImages;
      const nextTab = this.detailTabs(saved)[0]?.id || '';
      this.activeDetailTabId = nextTab;
      if (!this.hasTextDetails(saved)) {
        this.closeTextDetails();
      }
    } catch {
      this.errorMessage = 'Bild konnte nicht gelöscht werden.';
    }
  }

  async removeDetailLink(linkId: string): Promise<void> {
    if (!this.selectedFieldId || !this.detailText) return;

    const images = this.parseFieldTextImages(this.detailText);
    const links = this.parseFieldTextLinks(this.detailText).filter((link) => link.id !== linkId);
    const payload: FieldText = {
      ...this.detailText,
      bilderJson: this.createFieldTextMediaJson(images, links)
    };

    this.errorMessage = '';
    try {
      const saved = await firstValueFrom(this.api.updateFieldText(this.selectedFieldId, payload.feldTextId, payload));
      this.selectedFieldTexts = this.selectedFieldTexts.map((text) => text.feldTextId === saved.feldTextId ? saved : text);
      this.detailText = saved;
      this.textFormLinks = this.textEditMode === 'update' && this.textFormModel.feldTextId === saved.feldTextId
        ? this.parseFieldTextLinks(saved)
        : this.textFormLinks;
      const nextTab = this.detailTabs(saved)[0]?.id || '';
      this.activeDetailTabId = nextTab;
      if (!this.hasTextDetails(saved)) {
        this.closeTextDetails();
      }
    } catch {
      this.errorMessage = 'Link konnte nicht gelöscht werden.';
    }
  }

  private createFieldTextMediaJson(images = this.textFormImages, links = this.textFormLinks): string {
    if (!images.length && !links.length) return '';
    return JSON.stringify({
      images,
      links
    });
  }

  private processTextImageFiles(files: FileList | File[] | undefined | null): void {
    this.textImageUploadError = '';
    const selectedFiles = Array.from(files ?? []);
    if (!selectedFiles.length) return;

    selectedFiles.forEach((file) => {
      if (!this.isImageFile(file)) {
        this.textImageUploadError = 'Nur Bilddateien sind erlaubt.';
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const imageDataUrl = reader.result?.toString();
        if (!imageDataUrl) {
          this.textImageUploadError = 'Bild konnte nicht geladen werden.';
          return;
        }

        this.textFormImages = [
          ...this.textFormImages,
          {
            id: this.createClientId(),
            name: file.name || 'Bild',
            imageDataUrl
          }
        ];
      };
      reader.onerror = () => {
        this.textImageUploadError = 'Bild konnte nicht gelesen werden.';
      };
      reader.readAsDataURL(file);
    });
  }

  private parseFieldTextImages(fieldText: FieldText): FieldTextImage[] {
    if (!fieldText.bilderJson?.trim()) return [];

    try {
      const parsed = JSON.parse(fieldText.bilderJson);
      const images = Array.isArray(parsed) ? parsed : parsed?.images;
      if (!Array.isArray(images)) return [];

      return images
        .map((item, index) => ({
          id: item?.id?.toString() || `image-${index}`,
          name: item?.name?.toString() || `Bild ${index + 1}`,
          imageDataUrl: item?.imageDataUrl?.toString() || ''
        }))
        .filter((item) => !!item.imageDataUrl);
    } catch {
      return [];
    }
  }

  private parseFieldTextLinks(fieldText: FieldText): FieldTextLink[] {
    if (!fieldText.bilderJson?.trim()) return [];

    try {
      const parsed = JSON.parse(fieldText.bilderJson);
      const links = Array.isArray(parsed) ? [] : parsed?.links;
      if (!Array.isArray(links)) return [];

      return links
        .map((item, index) => ({
          id: item?.id?.toString() || `link-${index}`,
          label: item?.label?.toString() || item?.url?.toString() || `Link ${index + 1}`,
          url: item?.url?.toString() || ''
        }))
        .filter((item) => !!item.url);
    } catch {
      return [];
    }
  }

  private isImageFile(file: File): boolean {
    if (file.type) {
      return file.type.startsWith('image/');
    }
    const extension = file.name?.split('.').pop()?.toLowerCase();
    return !!extension && ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(extension);
  }

  private createClientId(): string {
    return typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}
